import type { NeedCategory } from '../types/api'

/**
 * The three categories whose provider is an institution rather than a person.
 *
 * <p>They behave differently on both sides of the marketplace, which is the whole point of
 * separating them: a school is not teaching a subject at a time and a price per hour, it is
 * admitting a class for a year, and a company is not teaching at all.
 */
export const INSTITUTION_CATEGORIES: NeedCategory[] = [
  'SCHOOL_ADMISSION',
  'HIGHER_EDUCATION',
  'INTERNSHIPS',
]

export function isInstitutionCategory(category: NeedCategory): boolean {
  return INSTITUTION_CATEGORIES.includes(category)
}

/**
 * Which of the category's detail questions the *institution* answers, in the order they are asked.
 *
 * <p>A category's detail list in the taxonomy holds both sides' questions - a school's tuition fee
 * sits beside the admission year a family is looking for. This says which belong on the offer
 * form; everything else on the list belongs on the learner's need form.
 *
 * <p>The keys and their option lists come from GET /api/taxonomy, so this is only an ordering and
 * a label - it cannot invent a value the server would reject.
 */
export const OFFER_DETAIL_FIELDS: Partial<Record<NeedCategory, { key: string; label: string }[]>> = {
  // Classes are the items and the board has its own field, so a school is only asked its fee.
  SCHOOL_ADMISSION: [{ key: 'tuition_fee', label: 'Tuition fee' }],

  HIGHER_EDUCATION: [
    { key: 'degree', label: 'Degree' },
    { key: 'programme_level', label: 'Programme level' },
    { key: 'course_duration', label: 'Course duration' },
    { key: 'min_qualification', label: 'Minimum qualification' },
    { key: 'tuition_fee', label: 'Tuition fee' },
  ],

  INTERNSHIPS: [
    { key: 'positions', label: 'Number of positions' },
    { key: 'min_qualification', label: 'Minimum qualification' },
    { key: 'stream', label: 'Stream' },
    { key: 'stipend_band', label: 'Stipend' },
    { key: 'duration', label: 'Duration' },
    { key: 'mode', label: 'Mode' },
  ],
}

/**
 * Detail questions that belong to the institution's offer and never to a learner's need.
 *
 * <p>A category's detail list holds both sides' questions in one place, because they are the same
 * vocabulary. This is the half a learner must not be asked: nobody looking for a school states
 * its tuition fee, and nobody looking for an internship states how many positions it has.
 *
 * <p>Some keys stay on both - a college's programme level and a company's duration and stipend
 * are asked of the learner as a preference and of the institution as a fact, and that is exactly
 * how the two get matched.
 */
export const OFFER_ONLY_DETAIL_KEYS: Partial<Record<NeedCategory, string[]>> = {
  SCHOOL_ADMISSION: ['tuition_fee'],
  HIGHER_EDUCATION: ['degree', 'course_duration', 'min_qualification', 'tuition_fee'],
  INTERNSHIPS: ['positions', 'min_qualification', 'stream', 'minimum_age'],
  // "haves" is what an entrepreneur's own offer (ENTREPRENEUR_SUPPORTER's ProviderService row)
  // is built from, translated server-side into offered items - see Taxonomy.java and
  // ProviderServiceService. It is never a question on the need side.
  ENTREPRENEURSHIP: ['haves'],
}

/**
 * Categories where none of mode / timing / budget / preferred-tutor-gender mean anything, because
 * the other side is not a person teaching by the hour: an institution admits a class or runs a
 * programme, and an entrepreneur's match is a co-founder, mentor or investor, not a tutoring
 * arrangement.
 */
export function hasTutorArrangement(category: NeedCategory): boolean {
  return !isInstitutionCategory(category) && category !== 'ENTREPRENEURSHIP'
}

/**
 * Questions on the learner's need form that do not apply to an institution category.
 *
 * <p>All four describe an arrangement with a tutor - how they teach, when, at what hourly price,
 * and whether the learner prefers a man or a woman. None of them means anything when the other
 * side is a school admitting a class or a company offering an internship.
 */
export const TUTOR_ONLY_NEED_FIELDS = ['mode', 'timing', 'budget', 'preferredTutor'] as const

/** The label a provider's own offer section carries, by what kind of participant they are. */
export const OFFER_SECTION_LABEL: Record<string, string> = {
  SCHOOL: 'Admissions',
  COLLEGE: 'Programmes',
  COMPANY: 'Internships',
}

/**
 * The single category an institution may offer in.
 *
 * <p>A school has exactly one thing to state and it is admissions. Leaving the category picker
 * open would let it list music lessons, and - worse in practice - opened the form on Academics,
 * so a school saw the whole tutor form and had to know to change a dropdown first.
 */
export const OFFER_CATEGORY_BY_ENTITY: Record<string, NeedCategory> = {
  SCHOOL: 'SCHOOL_ADMISSION',
  COLLEGE: 'HIGHER_EDUCATION',
  COMPANY: 'INTERNSHIPS',
}

/** One line under the heading, saying what this entity is actually publishing. */
export const OFFER_SECTION_BLURB: Record<string, string> = {
  SCHOOL: 'The classes you admit to, and on which board. This is what families nearby see.',
  COLLEGE: 'The programmes you want discovered, and who can apply for them.',
  COMPANY: 'The internships you want surfaced, and who can apply for them.',
}

/**
 * The list heading and its button, in the entity's own words.
 *
 * <p>"What I teach" and "Add a service" are a tutor's words. A school does not teach a service;
 * it admits a class, and a company does not teach at all.
 */
export const OFFER_LIST_COPY: Record<string, { heading: string; blurb: string; addLabel: string }> = {
  SCHOOL: {
    heading: 'What we offer',
    blurb: 'The classes and boards you admit to - families discover you by these.',
    addLabel: 'Add admissions',
  },
  COLLEGE: {
    heading: 'Our programmes',
    blurb: 'One entry per field of study - learners discover you by these.',
    addLabel: 'Add a programme',
  },
  COMPANY: {
    heading: 'Our internships',
    blurb: 'One entry per kind of work - learners discover you by these.',
    addLabel: 'Add an internship',
  },
}
